import React, { useEffect, useMemo, useState } from 'react';
import { useBcPermissionsAudit } from '../../hooks/queries';
import { apiClient } from '../../services/api';
import {
  buildMatrixColumnOptions,
  buildMetricColumnOptions,
  buildMetricWhoHasItOptions,
  buildPermissionGrantOptions,
  buildUserColumnOptions,
  buildUserNameOptions,
  buildUserPermissionOptions,
} from '../../utils/permissions-audit-filter-options';
import {
  filterAuditUsers,
  filterMatrixRows,
  filterMetricRows,
  countUniqueAuditUserEmails,
} from '../../utils/permissions-audit-filter-rows';
import {
  type ColumnFilters,
  hasActiveColumnFilters,
} from '../../utils/permissions-audit-filters';
import { buildPermissionMetrics } from '../../utils/permissions-audit-metrics';
import { buildPermissionMatrix, matrixCellValue } from '../../utils/permissions-audit-matrix';
import { downloadFilteredAuditExport } from '../../utils/permissions-audit-export';
import {
  formatStoreNameForDisplay,
  isInactiveUserStatus,
  isProductionStoreName,
} from '../../utils/permissions-audit-store-label';
import type { BcPermissionsAuditResponse, BcStoreUserRecord } from '../../services/api';
import { ColumnFilterDropdown } from './ColumnFilterDropdown';
import './BcUserPermissionsAudit.css';

type AuditView = 'matrix' | 'users' | 'metrics';

const MATRIX_FROZEN_COUNT = 4;
const USERS_FROZEN_COUNT = 5;

const STORE_FILTER_PANEL_MIN_WIDTH = 280;
const STORE_FILTER_PANEL_MAX_WIDTH = 360;

function frozenColumnClass(
  index: number,
  variant: 'matrix' | 'users',
  role: 'header' | 'filter' | 'cell' = 'cell',
): string {
  const lastIndex = variant === 'matrix' ? MATRIX_FROZEN_COUNT - 1 : USERS_FROZEN_COUNT - 1;
  return [
    'bc-permissions-audit__frozen-col',
    `bc-permissions-audit__frozen-col--${variant}-${index}`,
    index === lastIndex ? 'bc-permissions-audit__frozen-col--edge' : '',
    role === 'header' ? 'bc-permissions-audit__frozen-col--header' : '',
    role === 'filter' ? 'bc-permissions-audit__frozen-col--filter' : '',
  ]
    .filter(Boolean)
    .join(' ');
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message?: string }).message);
  }
  return 'Unable to load permissions audit data.';
}

function formatAuditSubtitle(data: BcPermissionsAuditResponse): string {
  const storeNames = data.stores
    .filter((store) => store.status === 'ok')
    .map((store) => formatStoreNameForDisplay(store.storeName))
    .sort((a, b) => a.localeCompare(b));

  if (storeNames.length === 1) {
    return `${storeNames[0]} — read-only audit`;
  }
  if (storeNames.length > 1) {
    return `${storeNames.join(', ')} — read-only audit`;
  }
  return `${formatStoreNameForDisplay(data.storeName)} — read-only audit`;
}

function inactiveRowClass(status: string): string {
  return isInactiveUserStatus(status) ? 'bc-permissions-audit__row--inactive' : '';
}

function StoreNameLabel({ storeName }: { storeName: string }): React.ReactElement {
  return (
    <>
      {storeName}
      {isProductionStoreName(storeName) ? (
        <span className="bc-permissions-audit__store-badge">Production</span>
      ) : null}
    </>
  );
}

function isStoreNameFilterActive(
  storeName: string,
  columnFilters: ColumnFilters,
): boolean {
  const selected = columnFilters.store_name ?? [];
  return selected.length === 1 && selected[0] === storeName;
}

function countPermissions(users: BcStoreUserRecord[]): { total: number; write: number } {
  let total = 0;
  let write = 0;
  for (const user of users) {
    total += user.permissions.length;
    write += user.permissions.filter((permission) => permission.isWrite).length;
  }
  return { total, write };
}

export function BcUserPermissionsAudit(): React.ReactElement {
  const { data, isLoading, error, isError } = useBcPermissionsAudit();
  const [view, setView] = useState<AuditView>('matrix');
  const [writeOnly, setWriteOnly] = useState(false);
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});

  useEffect(() => {
    setColumnFilters({});
  }, [view]);

  const setColumnFilter = (columnKey: string, selected: string[]): void => {
    setColumnFilters((previous) => ({ ...previous, [columnKey]: selected }));
  };

  const users = data?.users ?? [];
  const storeScopedUsers = useMemo(
    () =>
      filterAuditUsers(users, {
        store_hash: columnFilters.store_hash,
        store_name: columnFilters.store_name,
      }),
    [users, columnFilters.store_hash, columnFilters.store_name],
  );
  const metrics = useMemo(() => buildPermissionMetrics(storeScopedUsers), [storeScopedUsers]);
  const matrix = useMemo(() => buildPermissionMatrix(users, { writeOnly }), [users, writeOnly]);
  const metricRows = writeOnly ? metrics.rows.filter((row) => row.isWrite) : metrics.rows;
  const grantFilterOptions = useMemo(() => buildPermissionGrantOptions(), []);

  const filteredMatrixRows = useMemo(
    () => filterMatrixRows(matrix.rows, matrix.permissionColumns, columnFilters),
    [matrix.rows, matrix.permissionColumns, columnFilters],
  );
  const filteredUsers = useMemo(
    () => filterAuditUsers(users, columnFilters),
    [users, columnFilters],
  );
  const filteredMetricRows = useMemo(
    () => filterMetricRows(metricRows, columnFilters),
    [metricRows, columnFilters],
  );

  const hasActiveFilters = hasActiveColumnFilters(columnFilters);

  const displayedUserCount = countUniqueAuditUserEmails(
    hasActiveFilters ? filteredUsers : users,
  );
  const displayedPermissionCounts = hasActiveFilters
    ? countPermissions(filteredUsers)
    : {
        total: data?.totalPermissions ?? 0,
        write: data?.writePermissionCount ?? 0,
      };

  const canExport = !isLoading && !isError && users.length > 0;

  const handleMatrixExport = (): void => {
    if (!canExport) {
      return;
    }
    if (hasActiveFilters) {
      downloadFilteredAuditExport(
        'matrix',
        filteredUsers,
        filteredMatrixRows,
        matrix.permissionColumns,
      );
      return;
    }
    void apiClient.downloadBcPermissionsAuditExport('matrix');
  };

  const handleDetailExport = (): void => {
    if (!canExport) {
      return;
    }
    if (hasActiveFilters) {
      downloadFilteredAuditExport('detail', filteredUsers);
      return;
    }
    void apiClient.downloadBcPermissionsAuditExport('detail');
  };

  if (isLoading) {
    return (
      <div className="bc-permissions-audit">
        <div className="bc-permissions-audit__state">Loading store users and permissions…</div>
      </div>
    );
  }

  if (isError) {
    const message = getErrorMessage(error);
    return (
      <div className="bc-permissions-audit">
        <div className="bc-permissions-audit__state bc-permissions-audit__error">
          {message}
        </div>
        <p className="bc-permissions-audit__notice">
          Export is unavailable while the user list cannot be loaded.
        </p>
      </div>
    );
  }

  return (
    <div className="bc-permissions-audit">
      <div className="bc-permissions-audit__header">
        <div>
          <h1 className="bc-permissions-audit__title">BC Store User Permissions Audit</h1>
          <p className="bc-permissions-audit__subtitle">
            {data ? formatAuditSubtitle(data) : '— read-only audit'}
          </p>
        </div>
        <div className="bc-permissions-audit__actions">
          <button
            type="button"
            className="bc-permissions-audit__export-btn"
            onClick={handleMatrixExport}
            disabled={!canExport}
          >
            Download matrix
          </button>
          <button
            type="button"
            className="bc-permissions-audit__export-btn bc-permissions-audit__export-btn--secondary"
            onClick={handleDetailExport}
            disabled={!canExport}
          >
            Download detail CSV
          </button>
        </div>
      </div>

      {data?.storeWarnings && data.storeWarnings.length > 0 && (
        <div className="bc-permissions-audit__warnings" role="status">
          <strong>Some stores could not be loaded:</strong>
          <ul className="bc-permissions-audit__warnings-list">
            {data.storeWarnings.map((warning) => (
              <li key={warning.storeHash} title={warning.storeHash}>
                {warning.storeName}: {warning.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && data.stores.filter((store) => store.status === 'ok').length > 1 && (
        <div className="bc-permissions-audit__store-pills" role="group" aria-label="Filter by store">
          <button
            type="button"
            className={`bc-permissions-audit__store-pill ${
              !(columnFilters.store_name?.length) ? 'is-active' : ''
            }`}
            onClick={() => setColumnFilter('store_name', [])}
          >
            All stores
          </button>
          {data.stores
            .filter((store) => store.status === 'ok')
            .sort((a, b) => a.storeName.localeCompare(b.storeName))
            .map((store) => (
              <button
                key={store.storeHash}
                type="button"
                title={store.storeHash}
                aria-label={formatStoreNameForDisplay(store.storeName)}
                className={`bc-permissions-audit__store-pill ${
                  isStoreNameFilterActive(store.storeName, columnFilters) ? 'is-active' : ''
                }`}
                onClick={() => setColumnFilter('store_name', [store.storeName])}
              >
                <StoreNameLabel storeName={store.storeName} />
              </button>
            ))}
        </div>
      )}

      <div className="bc-permissions-audit__summary">
        <div className="bc-permissions-audit__summary-item">
          <span className="bc-permissions-audit__summary-label">Users</span>
          <span className="bc-permissions-audit__summary-value">{displayedUserCount}</span>
        </div>
        <div className="bc-permissions-audit__summary-item">
          <span className="bc-permissions-audit__summary-label">Permissions</span>
          <span className="bc-permissions-audit__summary-value">
            {displayedPermissionCounts.total}
          </span>
        </div>
        <div className="bc-permissions-audit__summary-item">
          <span className="bc-permissions-audit__summary-label">Write permissions</span>
          <span className="bc-permissions-audit__summary-value">
            {displayedPermissionCounts.write}
          </span>
        </div>
        {view === 'matrix' && (
          <div className="bc-permissions-audit__summary-item">
            <span className="bc-permissions-audit__summary-label">Matrix columns</span>
            <span className="bc-permissions-audit__summary-value">
              {matrix.permissionColumns.length}
            </span>
          </div>
        )}
        {view === 'metrics' && (
          <>
            <div className="bc-permissions-audit__summary-item">
              <span className="bc-permissions-audit__summary-label">Unique permissions</span>
              <span className="bc-permissions-audit__summary-value">
                {metrics.summary.uniquePermissions}
              </span>
            </div>
            <div className="bc-permissions-audit__summary-item">
              <span className="bc-permissions-audit__summary-label">Users with write access</span>
              <span className="bc-permissions-audit__summary-value">
                {metrics.summary.usersWithWriteAccess}
              </span>
            </div>
          </>
        )}
        {hasActiveFilters && (
          <div className="bc-permissions-audit__summary-item">
            <span className="bc-permissions-audit__summary-label">Filtered rows</span>
            <span className="bc-permissions-audit__summary-value">
              {view === 'matrix'
                ? filteredMatrixRows.length
                : view === 'users'
                  ? filteredUsers.length
                  : filteredMetricRows.length}
            </span>
          </div>
        )}
      </div>

      <div className="bc-permissions-audit__toolbar">
        <div className="bc-permissions-audit__view-toggle" role="tablist" aria-label="Audit view">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'matrix'}
            className={`bc-permissions-audit__view-btn ${view === 'matrix' ? 'is-active' : ''}`}
            onClick={() => setView('matrix')}
          >
            Matrix
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'users'}
            className={`bc-permissions-audit__view-btn ${view === 'users' ? 'is-active' : ''}`}
            onClick={() => setView('users')}
          >
            By user
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'metrics'}
            className={`bc-permissions-audit__view-btn ${view === 'metrics' ? 'is-active' : ''}`}
            onClick={() => setView('metrics')}
          >
            By permission
          </button>
        </div>
        <div className="bc-permissions-audit__toolbar-actions">
          {(view === 'matrix' || view === 'metrics') && (
            <label className="bc-permissions-audit__filter">
              <input
                type="checkbox"
                checked={writeOnly}
                onChange={(event) => setWriteOnly(event.target.checked)}
              />
              Write permissions only
            </label>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className="bc-permissions-audit__clear-filters"
              onClick={() => setColumnFilters({})}
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {users.length === 0 ? (
        <div className="bc-permissions-audit__state">
          No store users returned for this account.
          <p className="bc-permissions-audit__notice">Export is disabled when the list is empty.</p>
        </div>
      ) : view === 'matrix' ? (
        <div className="bc-permissions-audit__matrix-wrap">
          <table className="bc-permissions-audit__table bc-permissions-audit__table--matrix">
            <thead>
              <tr>
                <th className={frozenColumnClass(0, 'matrix', 'header')}>Store</th>
                <th className={frozenColumnClass(1, 'matrix', 'header')}>store_hash</th>
                <th className={frozenColumnClass(2, 'matrix', 'header')}>email</th>
                <th className={frozenColumnClass(3, 'matrix', 'header')}>status</th>
                {matrix.permissionColumns.map((code) => (
                  <th
                    key={code}
                    className={`bc-permissions-audit__perm-col ${
                      matrix.columnMeta[code]?.isWrite ? 'bc-permissions-audit__perm-col--write' : ''
                    }`}
                    title={code}
                  >
                    {code}
                  </th>
                ))}
              </tr>
              <tr className="bc-permissions-audit__filter-row">
                <th className={frozenColumnClass(0, 'matrix', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="store_name"
                    label="Store"
                    options={buildMatrixColumnOptions(matrix.rows, 'store_name')}
                    selected={columnFilters.store_name ?? []}
                    onChange={setColumnFilter}
                    compact
                    panelMinWidth={STORE_FILTER_PANEL_MIN_WIDTH}
                    panelMaxWidth={STORE_FILTER_PANEL_MAX_WIDTH}
                  />
                </th>
                <th className={frozenColumnClass(1, 'matrix', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="store_hash"
                    label="store_hash"
                    options={buildMatrixColumnOptions(matrix.rows, 'store_hash')}
                    selected={columnFilters.store_hash ?? []}
                    onChange={setColumnFilter}
                    compact
                  />
                </th>
                <th className={frozenColumnClass(2, 'matrix', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="email"
                    label="email"
                    options={buildMatrixColumnOptions(matrix.rows, 'email')}
                    selected={columnFilters.email ?? []}
                    onChange={setColumnFilter}
                    compact
                  />
                </th>
                <th className={frozenColumnClass(3, 'matrix', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="status"
                    label="status"
                    options={buildMatrixColumnOptions(matrix.rows, 'status')}
                    selected={columnFilters.status ?? []}
                    onChange={setColumnFilter}
                    compact
                  />
                </th>
                {matrix.permissionColumns.map((code) => (
                  <th key={`filter-${code}`} className="bc-permissions-audit__perm-col">
                    <ColumnFilterDropdown
                      columnKey={code}
                      label={code}
                      options={grantFilterOptions}
                      selected={columnFilters[code] ?? []}
                      onChange={setColumnFilter}
                      compact
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredMatrixRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={4 + matrix.permissionColumns.length}
                    className="bc-permissions-audit__empty-filter"
                  >
                    No rows match the current filters.
                  </td>
                </tr>
              ) : (
                filteredMatrixRows.map((row) => (
                  <tr
                    key={`${row.storeHash}-${row.email}`}
                    className={inactiveRowClass(row.status)}
                  >
                    <td className={frozenColumnClass(0, 'matrix')} title={row.storeName}>
                      <StoreNameLabel storeName={row.storeName} />
                    </td>
                    <td className={frozenColumnClass(1, 'matrix')}>{row.storeHash}</td>
                    <td className={frozenColumnClass(2, 'matrix')}>{row.email}</td>
                    <td className={frozenColumnClass(3, 'matrix')}>{row.status}</td>
                    {matrix.permissionColumns.map((code) => {
                      const granted = row.cells[code] ?? false;
                      const isWrite = matrix.columnMeta[code]?.isWrite ?? false;
                      return (
                        <td
                          key={`${row.email}-${code}`}
                          className={`bc-permissions-audit__matrix-cell ${
                            granted && isWrite ? 'bc-permissions-audit__matrix-cell--write' : ''
                          } ${granted ? 'bc-permissions-audit__matrix-cell--granted' : ''}`}
                        >
                          {matrixCellValue(granted)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : view === 'users' ? (
        <div className="bc-permissions-audit__table-wrap">
          <table className="bc-permissions-audit__table bc-permissions-audit__table--users">
            <thead>
              <tr>
                <th className={frozenColumnClass(0, 'users', 'header')}>Store</th>
                <th className={frozenColumnClass(1, 'users', 'header')}>store_hash</th>
                <th className={frozenColumnClass(2, 'users', 'header')}>Email</th>
                <th className={frozenColumnClass(3, 'users', 'header')}>Name</th>
                <th className={frozenColumnClass(4, 'users', 'header')}>Status</th>
                <th>Permissions</th>
              </tr>
              <tr className="bc-permissions-audit__filter-row">
                <th className={frozenColumnClass(0, 'users', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="store_name"
                    label="Store"
                    options={buildUserColumnOptions(users, 'store_name')}
                    selected={columnFilters.store_name ?? []}
                    onChange={setColumnFilter}
                    panelMinWidth={STORE_FILTER_PANEL_MIN_WIDTH}
                    panelMaxWidth={STORE_FILTER_PANEL_MAX_WIDTH}
                  />
                </th>
                <th className={frozenColumnClass(1, 'users', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="store_hash"
                    label="store_hash"
                    options={buildUserColumnOptions(users, 'store_hash')}
                    selected={columnFilters.store_hash ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
                <th className={frozenColumnClass(2, 'users', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="email"
                    label="Email"
                    options={buildUserColumnOptions(users, 'email')}
                    selected={columnFilters.email ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
                <th className={frozenColumnClass(3, 'users', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="name"
                    label="Name"
                    options={buildUserNameOptions(users)}
                    selected={columnFilters.name ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
                <th className={frozenColumnClass(4, 'users', 'filter')}>
                  <ColumnFilterDropdown
                    columnKey="status"
                    label="Status"
                    options={buildUserColumnOptions(users, 'status')}
                    selected={columnFilters.status ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
                <th>
                  <ColumnFilterDropdown
                    columnKey="permissions"
                    label="Permissions"
                    options={buildUserPermissionOptions(users)}
                    selected={columnFilters.permissions ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="bc-permissions-audit__empty-filter">
                    No rows match the current filters.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={`${user.id}-${user.storeHash}`}
                    className={inactiveRowClass(user.status)}
                  >
                    <td className={frozenColumnClass(0, 'users')} title={user.storeName}>
                      <StoreNameLabel storeName={user.storeName} />
                    </td>
                    <td className={frozenColumnClass(1, 'users')}>{user.storeHash}</td>
                    <td className={frozenColumnClass(2, 'users')}>{user.email}</td>
                    <td className={frozenColumnClass(3, 'users')}>
                      {user.firstName} {user.lastName}
                    </td>
                    <td className={frozenColumnClass(4, 'users')}>{user.status}</td>
                    <td>
                      <div className="bc-permissions-audit__permissions">
                        {user.permissions.length === 0 ? (
                          <span className="permission-chip permission-chip--unknown">none</span>
                        ) : (
                          user.permissions.map((permission) => (
                            <span
                              key={`${user.id}-${permission.code}`}
                              className={`permission-chip ${
                                permission.isWrite
                                  ? 'permission-chip--write'
                                  : permission.accessType === 'read'
                                    ? 'permission-chip--read'
                                    : 'permission-chip--unknown'
                              }`}
                              data-testid={`permission-${permission.code}`}
                            >
                              {permission.code}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bc-permissions-audit__table-wrap">
          <table className="bc-permissions-audit__table">
            <thead>
              <tr>
                <th>Permission</th>
                <th>Type</th>
                <th>Users</th>
                <th>Who has it</th>
              </tr>
              <tr className="bc-permissions-audit__filter-row">
                <th>
                  <ColumnFilterDropdown
                    columnKey="permission"
                    label="Permission"
                    options={buildMetricColumnOptions(metricRows, 'permissionCode')}
                    selected={columnFilters.permission ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
                <th>
                  <ColumnFilterDropdown
                    columnKey="type"
                    label="Type"
                    options={buildMetricColumnOptions(metricRows, 'accessType')}
                    selected={columnFilters.type ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
                <th>
                  <ColumnFilterDropdown
                    columnKey="users"
                    label="Users"
                    options={buildMetricColumnOptions(metricRows, 'userCount')}
                    selected={columnFilters.users ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
                <th>
                  <ColumnFilterDropdown
                    columnKey="whoHasIt"
                    label="Who has it"
                    options={buildMetricWhoHasItOptions(metricRows)}
                    selected={columnFilters.whoHasIt ?? []}
                    onChange={setColumnFilter}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredMetricRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="bc-permissions-audit__empty-filter">
                    No rows match the current filters.
                  </td>
                </tr>
              ) : (
                filteredMetricRows.map((row) => (
                  <tr key={row.permissionCode}>
                    <td>
                      <span
                        className={`permission-chip ${
                          row.isWrite
                            ? 'permission-chip--write'
                            : row.accessType === 'read'
                              ? 'permission-chip--read'
                              : 'permission-chip--unknown'
                        }`}
                      >
                        {row.permissionCode}
                      </span>
                    </td>
                    <td>{row.accessType}</td>
                    <td>{row.userCount}</td>
                    <td className="bc-permissions-audit__metric-users">{row.users.join(', ')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
